import { useCallback } from 'react';

import { useTrackCaptionGenerationCompleted } from '@air/analytics';

import { useAssetTransformationTaskGalleryUpdate } from '~/components/AssetTransformationTasks/useAssetTransformationTaskGalleryUpdate';
import { isCaptionVideoTask } from '~/components/CaptionVideoTasks/isCaptionVideoTask';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type SharedSyncTasksParams } from '~/utils/taskUtils/useSharedSyncTasks';

type OnCompleteParams = Parameters<
  NonNullable<SharedSyncTasksParams<'AssetTransformation', 'AssetTransformer'>['onComplete']>
>[0];

const getCompletedVersionId = (data: OnCompleteParams['task']['data']) => {
  if (data && 'versionId' in data && typeof data.versionId === 'string') {
    return data.versionId;
  }

  return undefined;
};

export const useCaptionVideoTaskGalleryUpdate = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackCaptionGenerationCompleted } = useTrackCaptionGenerationCompleted();
  const { onTaskComplete: onGalleryUpdateComplete } = useAssetTransformationTaskGalleryUpdate(isCaptionVideoTask);

  const onCaptionVideoTaskComplete = useCallback(
    async (params: OnCompleteParams) => {
      const { task, localTask } = params;

      if (!isCaptionVideoTask(localTask)) {
        return;
      }

      const clip = await onGalleryUpdateComplete(params);

      const versionId = getCompletedVersionId(task.data);

      if (!versionId || !currentWorkspace?.id) {
        return;
      }

      trackCaptionGenerationCompleted({
        video_id: localTask.metadata.assetVersionId,
        duration_seconds: clip?.duration ?? 0,
        style_config: JSON.stringify(localTask.metadata.options.style),
      });
    },
    [currentWorkspace?.id, onGalleryUpdateComplete, trackCaptionGenerationCompleted],
  );

  return { onCaptionVideoTaskComplete };
};
